import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import {
	RssFeedModel,
	RssInterestedParty,
	RssLiveInformationProvider
} from "client_legacy/Workspace/LiveInformation/Rss/provider";
import { TileContext } from "../TileContext";
import { Guid } from "client_legacy/ndaw/guid";
import { TileTabProps } from "../TileTabSelector";
import { isNil, isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { RssListItem } from "./RssListItem";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import {
	AlertManager,
	useAlerts,
	useUserTimezone
} from "client/Hooks";
import { BusyIndicator } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { RssTileTabModel } from "client/Api/Workspace/workspace";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type RssLiveTileContentProps = {
	provider: RssLiveInformationProvider;
	tabs: RssTileTabModel[];
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const RssLiveTileContent = (props: RssLiveTileContentProps): ReactElement => {
	const {
		provider,
		tabs,
		onEdit,
		onOpenTileMenu
	} = props;

	const {
		icon,
		id,
		name,
		environmentAppId,
		dimension: dimensionToRender,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(tabs[0]?.uniqueId);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});
	const [message, setMessage] = useState<string>("");
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension: dimensionToRender,
		appIsLicensed,
		appIsNotAccessible
	});

	const isCompactView: boolean = dimensionToRender.dimension.columnSpan < 6 || dimensionToRender.kind === "responsive";

	const {
		isLoading,
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		response
	} = loadingState;

	const activeTab: RssTileTabModel | undefined = tabs.find(x => x.uniqueId === activeTabId);

	const tabProps = useMemo(
		(): readonly TileTabProps[] => {
			return tabs.map(x => {
				const result: TileTabProps = {
					uniqueId: x.uniqueId,
					name: x.name
				};

				return result;
			});
		},
		[tabs]
	);

	const updateFeed = useCallback(
		(feedModel: RssFeedModel): void => {
			if (feedModel.tabUniqueId !== activeTabId) {
				return;
			}

			setLoadingState({
				isLoading: false,
				hasGlobalFailure: false,
				hasTooManyConsecutiveErrors: false,
				response: feedModel
			});

		},
		[activeTabId]
	);

	const parties = useMemo(
		(): readonly RssInterestedParty[] => {
			return tabs.map(tab => {
				return {
					updateFeed: (model) => {
						updateFeed(model);
					},
					resourceId: {
						tileId: id,
						tabId: tab.uniqueId
					},
					partyId: tab.uniqueId,
					informAboutGlobalRetrievalFailure: () => {
						updateFeed({
							status: "error",
							tabUniqueId: tab.uniqueId
						});
					},
					informAboutConsecutiveErrors: () => {
						setLoadingState((x) => ({
							...x,
							hasTooManyConsecutiveErrors: true
						}));
					}
				};
			});
		},
		[
			tabs,
			id,
			updateFeed
		]
	);

	useEffect(
		(): (() => void) => {
			parties.forEach(party => {
				provider.register(party, {});
			});

			return () => {
				parties.forEach(party => {
					provider.unregister(party);
				});
			};
		},
		[parties, provider]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			if (isNil(activeTabId)) {
				return;
			}

			const party: RssInterestedParty | undefined = parties
				.find(party => party.partyId === activeTabId);

			if(!party) {
				throw new Error("Active party was not found");
			}

			provider.changeInterests(party, {items: true});

			return (): void => provider.changeInterests(party, {});
		},
		[
			activeTabId,
			parties,
			provider
		]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			if (!activeTabId) {
				alertManager.showError(localize("_GeneralErrorMessage"));
				return;
			}
			navigateRelativeToEnvironment("/Workspace/Go/" + id + "/Tab/" + encodeURIComponent(activeTabId), true);
		},
		[
			activeTabId,
			id,
			alertManager,
			addAnalyticsEvent
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const isTabsAvailable: boolean = tabs.length > 0;

	useEffect(
		(): void => {
			if (isTabsAvailable) {
				setActiveTabId(tabs[0].uniqueId);
			} else {
				const tileMessage: string = localize(
					!onEdit
						? "_RssTileHasNoFeedsConfiguredContactAdminMessage"
						: "_RssTileHasNoFeedsConfiguredMessage"
				);
				setMessage(tileMessage);
			}

		},
		[
			onEdit,
			isTabsAvailable,
			tabs
		]
	);

	const renderTabContent = (): ReactElement | null => {
		if (isNil(activeTab)) {
			return null;
		}

		if (isLoading) {
			return null;
		}

		if (hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (hasGlobalFailure || response?.status === "error") {
			return <LiveTileContent.Message text={localize("_RssFeedRetrievalError")} />;
		}

		if (response?.items.length === 0) {
			return <LiveTileContent.Message text={localize("_NoItemsToShow")} />;
		}

		if (!isTabsAvailable) {
			return (
				<LiveTileContent.Message text={message} />
			);
		}

		return (
			<LiveTileContent.List>
				{response?.items
					.map((x, index) => (
						<RssListItem
							key={index}
							item={x}
							isCompactView={isCompactView}
							timezone={userTimezone}
							onClick={addAnalyticsEvent}
						/>
					))
				}
			</LiveTileContent.List>
		);
	};

	return (
		<LiveTileContent
			icon={icon}
			name={name}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					tileId={id}
					tileName={name}
					value={activeTabId}
					onChange={setActiveTabId}
					tabs={tabProps}
				/>
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{isLoading && isNotNil(activeTab) && (
							<BusyIndicator />
						)}
						<LiveTileContent.Scrollable>
							{renderTabContent()}
						</LiveTileContent.Scrollable>
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			{isNotNil(activeTab) && (
				<LiveTileContent.Footer
					name={activeTab.name}
					onClick={navigateHandler}
				/>
			)}
		</LiveTileContent>
	);
};
RssLiveTileContent.displayName = "RssLiveTileContent";

type LoadingState = {
	isLoading?: boolean;
	hasTooManyConsecutiveErrors?: boolean;
	hasGlobalFailure?: boolean;
	response?: RssFeedModel;
};